import {
  loadAdvancedSearchQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  SearchEngine,
} from '@coveo/atomic-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type UseCategoryFilterReturn = {
  currentCategoryPath: string[];
  applyFilter: (categoryPath: string[]) => void;
  clearFilter: () => void;
  isApplying: boolean;
};

/**
 * Hook to apply category filters to search results using advancedQuery.
 * Does NOT affect the category facet tree -- filtering is independent.
 */
export function useCategoryFilter(
  searchEngine: SearchEngine | undefined
): UseCategoryFilterReturn {
  const actions = useMemo(() => {
    if (!searchEngine) {
      return null;
    }
    return {
      advanced: loadAdvancedSearchQueryActions(searchEngine),
      search: loadSearchActions(searchEngine),
      analytics: loadSearchAnalyticsActions(searchEngine),
    };
  }, [searchEngine]);

  const [currentCategoryPath, setCurrentCategoryPath] = useState<string[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!searchEngine) {
      return;
    }

    const unsubscribe = searchEngine.subscribe(() => {
      const aqFromState = searchEngine.state?.advancedSearchQueries?.aq;

      if (aqFromState) {
        const categoryMatch = aqFromState.match(/@ec_category=="([^"]+)"/);
        if (categoryMatch?.[1]) {
          setCurrentCategoryPath(categoryMatch[1].split(','));
        }
      } else {
        setCurrentCategoryPath([]);
      }
    });

    return unsubscribe;
  }, [searchEngine]);

  const applyFilter = useCallback(
    (categoryPath: string[]) => {
      if (!searchEngine || !actions) {
        return;
      }

      setIsApplying(true);
      setCurrentCategoryPath(categoryPath);

      try {
        const filterQuery =
          categoryPath.length > 0
            ? `@ec_category=="${categoryPath.join(',')}"`
            : '';

        searchEngine.dispatch(
          actions.advanced.updateAdvancedSearchQueries({ aq: filterQuery })
        );

        const analyticsAction = actions.analytics.logInterfaceChange();
        searchEngine.dispatch(actions.search.executeSearch(analyticsAction));
      } finally {
        setIsApplying(false);
      }
    },
    [searchEngine, actions]
  );

  const clearFilter = useCallback(() => {
    applyFilter([]);
  }, [applyFilter]);

  return {
    currentCategoryPath,
    applyFilter,
    clearFilter,
    isApplying,
  };
}
